
import VueFilters from "@/vue-filters";
import { Component, Prop, Vue } from "vue-property-decorator";
import { BvTableFieldArray } from "bootstrap-vue";
import {
  ICountryConfig,
  IDeclaredValue,
  IDeclaredValueFingerprint,
  ISolCustomerCommodity,
  ISolCustomerCurrency,
  IUserDetailData,
  IValue,
} from "@/entities";
import { mapGetters } from "vuex";
import IconTrash from "@/components/common/icon/IconTrash.vue";
import { Getter } from "vuex-class";
import IconEdit from "@/components/common/icon/IconEdit.vue";
import DeclaredValueDetails from "@/components/orders/agile_pickup/DeclaredValueDetails.vue";
import AddEditDeclaredValueModal from "@/components/orders/agile_pickup/AddEditDeclaredValueModal.vue";
import { IDeleteDeclaredValueRequestBody } from "@/services";
import { SweetAlertResult } from "sweetalert2";
import ClientsFilter from "@/components/client/ClientsFilter.vue";
import IconQr from "@/components/common/icon/IconQr.vue";
import DeclaredContainerDetails from "@/components/orders/agile_pickup/DeclaredContainerDetails.vue";

@Component({
  name: "agile-pickup-declared-value-table",
  mixins: [VueFilters],
  computed: {
    ...mapGetters(["getLocale"]),
  },
  components: {
    DeclaredContainerDetails,
    IconQr,
    ClientsFilter,
    AddEditDeclaredValueModal,
    DeclaredValueDetails,
    IconEdit,
    IconTrash,
  },
})
export default class AgilePickupDeclaredValueTable extends Vue {
  @Prop({ required: true, default: "", type: String }) serviceCode!: string;
  @Prop({ required: true }) commodities!: ISolCustomerCommodity[];
  @Prop({ required: true }) agilePickUpAvailable!: boolean;
  @Prop({ required: false, default: "0", type: String }) mode!: string;
  @Prop({ required: true, default: "", type: String }) serviceStatusCode!: string;
  @Prop({ required: true }) declaredValues!: IDeclaredValueFingerprint | null;
  @Prop({ required: true }) loading!: boolean;

  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig;
  @Getter("getSolCustomerCurrencies") solCustomerCurrencies!: ISolCustomerCurrency[];

  public selectedDeclaredValue: IDeclaredValue | null = null;

  public collapsed = true;

  get canAddOrEditDeclaredValues(): boolean {
    return ["PENDING", "RECEIVED", "PREPARING", "PREPARED", "PLANNED"].includes(
      this.serviceStatusCode
    );
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first align-middle position-relative",
      },
      {
        key: "co",
        label: this.$t("column.code") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "values",
        label: this.$t("column.declaredTotal") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last align-middle",
      },
    ];
  }

  async refreshData(): Promise<void> {
    this.$emit("refresh");
  }

  getCurrencySymbol(currencyIsoCode: string): string {
    const currency = this.solCustomerCurrencies.find(
      (currency) => currency.isoCode === currencyIsoCode
    );
    return currency?.symbol || "";
  }

  openNewDeclaredValueModal(): void {
    this.selectedDeclaredValue = null;
    this.$nextTick(() => {
      this.$bvModal.show("addEditDeclaredValueModal");
    });
  }
  onClickEdit(declaredValue: IDeclaredValue): void {
    this.selectedDeclaredValue = declaredValue;
    this.$nextTick(() => {
      this.$bvModal.show("addEditDeclaredValueModal");
    });
  }

  sumAmounts(values: IValue[] | undefined): { amount: number; currencyIsoCode: string }[] {
    const amountsByCurrency: { amount: number; currencyIsoCode: string }[] = [];

    values?.forEach((value) => {
      const amountByCurrency = amountsByCurrency.find(
        (amountByCurrency) => amountByCurrency.currencyIsoCode === value.cic
      );

      if (amountByCurrency) {
        amountByCurrency.amount = (amountByCurrency.amount ?? 0) + (value.am ?? 0);
      } else {
        amountsByCurrency.push({
          amount: value.am ?? 0,
          currencyIsoCode: value.cic ?? "",
        });
      }
    });
    return amountsByCurrency;
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("message.deleteDeclaredValue.confirm.title").toString(),
      html: this.$t("message.deleteDeclaredValue.confirm.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes").toString(),
      cancelButtonText: this.$t("label.no").toString(),
    });
  }

  async onClickDelete(declaredValue: IDeclaredValue): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();
    if (isConfirmed) {
      const body: IDeleteDeclaredValueRequestBody = {
        countryCode: this.getLoggedUser.country!.code!,
        serviceCode: this.serviceCode,
        declaredCode: [{ code: declaredValue.co! }],
      };
      let success = await this.$services.solServiceApi.deleteDeclaredValue(body);

      if (success) {
        await this.refreshData();
      }
    }
  }
}
